import React from 'react';
import { ReactComponent as Send } from 'assets/icons/upArrow.svg';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import ButtonComponent from 'components/button/buttonComponent';
import {
  ContentType,
  FileAttachmentInput,
  SupportChatDetailsOutput,
} from 'graphql/generated/hasura';
import { useGetConversation } from '../hooks/useGetConversation';
import {
  createAcceptPolicy,
  mapFileTypeToApiFileType,
} from 'utilities/fileHelpers';
import { useFormErrors } from 'components/stepper/useStepperForm';
import * as yup from 'yup';
import { MBite } from 'const/validation';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUploadFileApi } from 'hooks/useUploadFileApi';
import { useParams } from 'react-router-dom';
import AttachFile from '../../providers-view-selection/components/bio/messages/AttachFile';
import FormAttachButton from 'components/stepper/field-components/FormAttachButton';
import { FormTextarea } from 'components/stepper/field-components';
import FieldError from 'components/stepper/field-components/FieldError';

interface SendFormProps {
  handleSend: (message: string, file?: FileAttachmentInput) => void;
  disabled: boolean;
  isMaxMessages: boolean;
  chat?: SupportChatDetailsOutput | null;
  supportChatId?: string;
  disabledText?: string;
}

const MAX_FILE_SIZE = 50;
const MAX_SUPPORT_TICKET_MESSAGES = 1_000;

const types = [
  ContentType.Jpg,
  ContentType.Png,
  ContentType.Jpeg,
  ContentType.Heif,
  ContentType.Heic,

  ContentType.Pdf,
  ContentType.Doc,
  ContentType.Docx,
];
const ACCEPTED_FILE_TYPES = createAcceptPolicy(types);

const SendForm: React.FC<SendFormProps> = ({
  handleSend,
  disabled,
  isMaxMessages,
  chat,
  supportChatId,
  disabledText,
}) => {
  const { conversationId } = useParams();

  const { conversation } = useGetConversation(conversationId as string);
  const [loading, setLoading] = React.useState(false);
  const { uploadMessagesFile, uploadSupportFile } = useUploadFileApi();

  const isSupportChat = supportChatId === conversationId;

  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.MESSAGES,
  });
  const { onError } = useFormErrors();

  const schemas = yup.object().shape({
    message: yup.string().required('Field is required'),
    attachment: yup
      .mixed<FileList>()
      .test(
        'fileSize',
        locale?.invalidFileSize ??
          `File size exceeds the limit. Please upload a file smaller than ${MAX_FILE_SIZE}MB`,
        (value) => {
          if (!value || value.length === 0) return true;
          return value[0].size <= MAX_FILE_SIZE * MBite;
        },
      )
      .test(
        'fileFormat',
        `You have uploaded an invalid file type. Only files with the following extensions are allowed: ${types.join(
          ', ',
        )}`,
        (value) => {
          if (!value || value.length === 0) return true;
          const supportedFormats = Object.keys(ACCEPTED_FILE_TYPES);
          const fileExtension = value[0].type;
          return supportedFormats.includes(fileExtension);
        },
      ),
  });

  const method = useForm({
    resolver: yupResolver(schemas),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const handleOnSubmit = async (data: yup.InferType<typeof schemas>) => {
    setLoading(true);
    const file = data?.attachment?.[0];

    try {
      if (data.attachment && file) {
        const response = isSupportChat
          ? await uploadSupportFile(file, chat?.user?.codexId as string)
          : await uploadMessagesFile(
              file,
              conversation?.provider?.codexId as string,
              conversation?.patient?.codexId as string,
            );
        const { fileId, contentType, size, fileName: title } = response.file;
        const type = mapFileTypeToApiFileType[contentType] ?? ContentType.Jpg;
        return handleSend(data.message, {
          fileId,
          contentType: type,
          size,
          title,
        });
      }
      return handleSend(data.message);
    } finally {
      setLoading(false);
    }
  };

  const attach = useWatch({ control: method.control, name: 'attachment' });
  const message = useWatch({ control: method.control, name: 'message' });

  const onDeleteFile = () =>
    method.setValue('attachment', undefined, { shouldValidate: true });

  if (isMaxMessages) {
    return (
      <div className="py-5 text-center px-30 flex items-center justify-center bg-white desktop:bg-transparent border-t border-t-gray-opacity-15">
        {disabledText ?? locale?.allMessagesSend}
      </div>
    );
  }

  return (
    <FormProvider {...method}>
      <form
        className="flex items-end bg-white desktop:bg-transparent gap-2.5 p-30 pt-5 border-t border-t-gray-opacity-15"
        onSubmit={method.handleSubmit(
          (data) => handleOnSubmit(data).then(() => method.reset()),
          onError,
        )}
        noValidate
      >
        <div className="w-full flex flex-col gap-1.5 relative">
          {attach?.length && (
            <div className="absolute -top-4">
              <AttachFile
                onDelete={onDeleteFile}
                size={attach?.[0]?.size}
                title={attach?.[0]?.name}
              />
            </div>
          )}
          <Controller
            name="attachment"
            control={method.control}
            render={({ field }) => (
              <FormAttachButton
                className="absolute top-3.5 left-2.5"
                id="attachment"
                accept={ACCEPTED_FILE_TYPES}
                onDrop={(acceptedFiles, fileRejections) => {
                  if (fileRejections.length > 0) {
                    field.onChange(fileRejections.map(({ file }) => file));
                    return;
                  }
                  field.onChange(acceptedFiles);
                }}
              />
            )}
          />

          <FormTextarea
            name="message"
            className="pl-10 w-full max-h-[84px] overflow-y-auto scrollbar-thin scrollbar-thumb-rounded-full scrollbar-track-rounded-full"
            maxLength={MAX_SUPPORT_TICKET_MESSAGES}
            placeholder={locale?.messagesPlaceholder}
          />
          {method.formState?.errors?.attachment && (
            <FieldError className="!whitespace-normal">
              {method.formState?.errors?.attachment?.message}
            </FieldError>
          )}
          {!method.formState?.errors?.attachment && (
            <div className="w-full flex items-center justify-between absolute -bottom-5">
              <p className="text-xs bg-white desktop:bg-transparent flex items-center font-semibold text-med-gray-3">
                <span>
                  {message?.length ?? 0}/{MAX_SUPPORT_TICKET_MESSAGES}
                </span>
              </p>
            </div>
          )}
        </div>
        <ButtonComponent
          testID={'send'}
          Icon={Send}
          type="submit"
          iconPosition="right"
          containerClassName="!gap-1"
          className="px-2 min-h-[46px] !w-auto"
          iconHeight="fill-white rotate-90 h-[5px] stroke-white"
          disabled={disabled || loading}
          showSpinner={loading}
        >
          <span className="max-desktop:hidden">{locale?.send}</span>
        </ButtonComponent>
      </form>
    </FormProvider>
  );
};

export default SendForm;
